import { useParams, Link } from "wouter";
import { usePageTitle } from "@/hooks/use-page-title";
import { useSettingsFormat } from "@/hooks/use-settings-format";
import {
  useGetProductUsage,
  useUpdateProduct,
  useDeleteProduct,
  useListProductDefaultCosts,
  useCreateProductDefaultCost,
  useUpdateProductDefaultCost,
  useDeleteProductDefaultCost,
  getListProductsQueryKey,
  getListProductDefaultCostsQueryKey,
  useListInternalCatalog,
} from "@workspace/api-client-react";
import type { ProductDefaultCost } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { format } from "date-fns";
import {
  ArrowLeft, Edit, Trash2, Loader2, Package,
  DollarSign, Tag, Layers, TrendingUp, BarChart3, Hash,
  EyeOff, Plus, Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ProGate } from "@/components/pro-gate";

const UNITS = ["each", "hour", "sqft", "lf", "day", "lb", "ton", "yd", "gal"];

const defaultCostSchema = z.object({
  catalogItemId: z.coerce.number().nullable().refine(val => val !== null && val > 0, {
    message: "Select an item from the cost catalog",
  }),
  name: z.string().min(1, "Required"),
  quantity: z.coerce.number().min(0.001),
  unit: z.string().optional(),
  unitCost: z.coerce.number().min(0),
  supplier: z.string().optional(),
  notes: z.string().optional(),
  type: z.enum(["labour", "materials", "fees"]).default("materials"),
});
type DefaultCostForm = z.infer<typeof defaultCostSchema>;

const productSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  unitPrice: z.coerce.number().min(0, "Price must be 0 or more").max(99999999.99, "Maximum value is 99,999,999"),
  unit: z.string().optional(),
  category: z.string().optional(),
  sku: z.string().optional().or(z.literal("")),
  taxable: z.boolean().optional(),
  productType: z.string().optional(),
});
type ProductForm = z.infer<typeof productSchema>;

const getStatusColor = (status: string) => {
  switch (status) {
    case "accepted": return "bg-green-100 text-green-800 border-green-200";
    case "sent": return "bg-blue-100 text-blue-800 border-blue-200";
    case "declined": return "bg-red-100 text-red-800 border-red-200";
    case "converted": return "bg-purple-100 text-purple-800 border-purple-200";
    case "paid": return "bg-green-100 text-green-800 border-green-200";
    case "partial": return "bg-yellow-100 text-yellow-800 border-yellow-200";
    case "overdue": return "bg-red-100 text-red-800 border-red-200";
    default: return "bg-gray-100 text-gray-800 border-gray-200";
  }
};

const getStatusLabel = (status: string) => {
  if (status === "converted") return "Converted";
  return status.charAt(0).toUpperCase() + status.slice(1);
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value);

type CostType = "labour" | "materials" | "fees";

function normalizeCostType(value: string | null | undefined): CostType {
  const v = (value ?? "").trim().toLowerCase();
  if (v === "labour" || v === "materials" || v === "fees") return v;
  return "materials";
}

function CostTypeBadge({ type, className = "" }: { type: string | null | undefined; className?: string }) {
  const normalized = normalizeCostType(type);
  const styles =
    normalized === "labour"
      ? "bg-blue-50 text-blue-700 border-blue-200"
      : normalized === "fees"
      ? "bg-orange-50 text-orange-700 border-orange-200"
      : "bg-green-50 text-green-700 border-green-200";
  const label = normalized === "labour" ? "Labor" : normalized === "fees" ? "Fees" : "Materials";
  return (
    <span
      className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded border font-medium uppercase tracking-wide ${styles} ${className}`}
    >
      {label}
    </span>
  );
}

export default function ProductDetail() {
  const params = useParams();
  const productId = parseInt(params.id || "0", 10);
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const { data: usage, isLoading } = useGetProductUsage(productId, {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    query: { enabled: !!productId } as any,
  });
  usePageTitle(usage?.name ?? "Product");

  const updateProduct = useUpdateProduct();
  const deleteProduct = useDeleteProduct();

  const form = useForm<ProductForm>({
    resolver: zodResolver(productSchema),
    values: {
      name: usage?.name || "",
      description: usage?.description || "",
      unitPrice: usage?.unitPrice || 0,
      unit: usage?.unit || "each",
      category: usage?.category || "",
      sku: usage?.sku || "",
      taxable: usage?.taxable ?? true,
      productType: usage?.productType || "service",
    },
  });

  const onUpdate = (data: ProductForm) => {
    updateProduct.mutate({
      id: productId,
      data: {
        name: data.name,
        description: data.description || undefined,
        unitPrice: data.unitPrice,
        unit: data.unit || undefined,
        category: data.category || undefined,
        sku: data.sku || undefined,
        taxable: data.taxable ?? true,
        productType: data.productType || undefined,
      },
    }, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListProductsQueryKey() });
        queryClient.invalidateQueries({ queryKey: [`/api/products/${productId}/usage`] });
        setIsEditOpen(false);
        toast({ title: "Product updated" });
      },
      onError: () => toast({ title: "Failed to update product", variant: "destructive" }),
    });
  };

  const onDelete = () => {
    deleteProduct.mutate({ id: productId }, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListProductsQueryKey() });
        toast({ title: "Product deleted" });
        setLocation("/products");
      },
      onError: () => toast({ title: "Failed to delete product", variant: "destructive" }),
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-32" />
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-8 w-64 mb-4" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-4 w-48" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!usage) {
    return <div className="text-center py-12">Product not found</div>;
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <Link href="/products">
        <Button variant="ghost" size="sm" className="gap-1.5 -ml-2 text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" />
          Back to Products
        </Button>
      </Link>

      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Package className="w-6 h-6 text-muted-foreground" />
            <h1 className="text-3xl font-bold tracking-tight text-foreground">{usage.name}</h1>
          </div>
          {usage.category && (
            <p className="text-muted-foreground flex items-center gap-1.5 mt-1 text-sm">
              <Layers className="w-3.5 h-3.5" />
              {usage.category}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setIsEditOpen(true)}>
            <Edit className="w-4 h-4 mr-2" />
            Edit
          </Button>
          <Button variant="destructive" onClick={() => setIsDeleteOpen(true)}>
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Item Info + Default Costs */}
        <div className="space-y-4">
          <Card className="shadow-sm border-border/50">
            <CardHeader>
              <CardTitle className="text-lg">Product Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-3 text-sm">
                <DollarSign className="w-4 h-4 text-muted-foreground mt-0.5" />
                <div>
                  <p className="font-medium text-foreground">Unit Price</p>
                  <p className="text-2xl font-bold text-primary">{formatCurrency(usage.unitPrice)}</p>
                  {usage.unit && <p className="text-xs text-muted-foreground">per {usage.unit}</p>}
                </div>
              </div>

              {usage.description && (
                <div className="flex items-start gap-3 text-sm">
                  <Tag className="w-4 h-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="font-medium text-foreground">Description</p>
                    <p className="text-muted-foreground mt-0.5">{usage.description}</p>
                  </div>
                </div>
              )}

              {usage.sku && (
                <div className="flex items-start gap-3 text-sm">
                  <Hash className="w-4 h-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="font-medium text-foreground">SKU</p>
                    <p className="text-muted-foreground font-mono">{usage.sku}</p>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-6 text-sm">
                {usage.productType && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Type</p>
                    <p className="text-foreground capitalize">{usage.productType === "non_inventory" ? "Non-Inventory" : "Service"}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Taxable</p>
                  <p className="text-foreground">{usage.taxable !== false ? "Yes" : "No"}</p>
                </div>
              </div>

              <div className="pt-2 text-xs text-muted-foreground border-t border-border/50">
                Added {format(new Date(usage.createdAt), "MMMM d, yyyy")}
              </div>
            </CardContent>
          </Card>

          <ProGate feature="PRODUCT_COST_ADD" mode="overlay">
            <DefaultCostsPanel productId={productId} />
          </ProGate>
        </div>

        {/* Stats & History */}
        <div className="md:col-span-2 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card className="bg-card sm:col-span-2">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="w-3.5 h-3.5 text-muted-foreground" />
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Invoice Revenue</p>
                </div>
                <p className="text-2xl font-bold">{formatCurrency(usage.invoiceRevenue)}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {usage.timesUsedOnInvoices} invoice{usage.timesUsedOnInvoices !== 1 ? "s" : ""}
                  {usage.timesUsedOnQuotes > 0 && (
                    <span className="ml-2 text-muted-foreground/70">
                      · also on {usage.timesUsedOnQuotes} quote{usage.timesUsedOnQuotes !== 1 ? "s" : ""}
                    </span>
                  )}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <BarChart3 className="w-3.5 h-3.5 text-muted-foreground" />
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Avg Qty / Use</p>
                </div>
                <p className="text-2xl font-bold">{usage.avgQty.toFixed(2)}</p>
                <p className="text-xs text-muted-foreground mt-1">across all uses</p>
              </CardContent>
            </Card>
          </div>

          <Card className="shadow-sm border-border/50">
            <CardHeader className="flex flex-row items-center justify-between pb-2 border-b bg-muted/40">
              <CardTitle className="text-lg">Usage History</CardTitle>
              {usage.usageHistory.length > 0 && (
                <span className="text-sm text-muted-foreground">
                  {usage.usageHistory.length} use{usage.usageHistory.length !== 1 ? "s" : ""}
                </span>
              )}
            </CardHeader>
            <CardContent className="p-0">
              {usage.usageHistory.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground text-sm">
                  This product hasn't been used on any invoices or quotes yet.
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {usage.usageHistory.map((row, i) => (
                    <Link key={i} href={row.source === "invoice" ? `/invoices/${row.documentId}` : `/quotes/${row.documentId}`}>
                      <div className="flex items-center justify-between p-4 hover:bg-muted/50 cursor-pointer transition-colors group">
                        <div className="min-w-0 pr-4">
                          <div className="flex items-center gap-2">
                            <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded border font-medium uppercase tracking-wide ${
                              row.source === "invoice"
                                ? "bg-purple-50 text-purple-700 border-purple-200"
                                : "bg-blue-50 text-blue-700 border-blue-200"
                            }`}>
                              {row.source === "invoice" ? "Invoice" : "Quote"}
                            </span>
                            {row.documentNumber && (
                              <span className="text-xs font-mono text-muted-foreground">{row.documentNumber}</span>
                            )}
                            <p className="font-medium truncate group-hover:text-primary transition-colors">
                              {row.documentTitle}
                            </p>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {row.customerName} &middot; {format(new Date(row.documentDate), "MMM d, yyyy")}
                          </p>
                        </div>
                        <div className="flex items-center gap-4 shrink-0">
                          <div className="text-right">
                            <p className="font-semibold">{formatCurrency(row.lineTotal)}</p>
                            <p className="text-xs text-muted-foreground">qty: {row.qty}</p>
                          </div>
                          <Badge variant="outline" className={`text-xs ${getStatusColor(row.status)}`}>
                            {getStatusLabel(row.status)}
                          </Badge>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Product</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onUpdate)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name *</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Default Description</FormLabel>
                    <FormControl>
                      <Textarea className="min-h-[72px] resize-none" {...field} />
                    </FormControl>
                    <FormDescription>Pre-fills the description when added to a quote.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="unitPrice"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Unit Price *</FormLabel>
                      <FormControl><Input type="number" step="0.01" min="0" {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="unit"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Unit</FormLabel>
                      <FormControl>
                        <Input placeholder="each" list="unit-options-detail" {...field} />
                      </FormControl>
                      <datalist id="unit-options-detail">
                        {UNITS.map(u => <option key={u} value={u} />)}
                      </datalist>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="sku"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>SKU</FormLabel>
                    <FormControl><Input placeholder="e.g. SVC-001" {...field} /></FormControl>
                    <FormDescription>Optional unique identifier for this product.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="productType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Type</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value ?? "service"}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="service">Service</SelectItem>
                          <SelectItem value="non_inventory">Non-Inventory</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="taxable"
                  render={({ field }) => (
                    <FormItem className="flex flex-col justify-end pb-1">
                      <FormLabel>Taxable</FormLabel>
                      <div className="flex items-center gap-2 mt-1">
                        <Switch checked={field.value ?? true} onCheckedChange={field.onChange} />
                        <span className="text-sm text-muted-foreground">{field.value ?? true ? "Yes" : "No"}</span>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsEditOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={updateProduct.isPending}>
                  {updateProduct.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Save Changes
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Delete Alert */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{usage.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the product from your catalog. Existing line items that used this product won't be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); onDelete(); }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteProduct.isPending}
            >
              {deleteProduct.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Delete Product
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function DefaultCostsPanel({ productId }: { productId: number }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { formatCurrency } = useSettingsFormat();
  const [addOpen, setAddOpen] = useState(false);
  const [editingCost, setEditingCost] = useState<ProductDefaultCost | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ProductDefaultCost | null>(null);

  const { data: costs, isLoading } = useListProductDefaultCosts(productId, {
    query: { queryKey: getListProductDefaultCostsQueryKey(productId) }
  });

  const deleteCost = useDeleteProductDefaultCost();

  const onDeleteCost = (costId: number) => {
    deleteCost.mutate({ productId, costId }, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListProductDefaultCostsQueryKey(productId) });
        setDeleteTarget(null);
        toast({ title: "Default cost removed" });
      },
      onError: () => toast({ title: "Failed to delete cost", variant: "destructive" }),
    });
  };

  const totalCost = costs?.reduce((s, c) => s + c.totalCost, 0) ?? 0;

  return (
    <Card className="shadow-sm border-border/50">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <EyeOff className="w-4 h-4 text-primary" />
            <CardTitle className="text-base">Default Costs</CardTitle>
            <Badge variant="outline" className="text-[10px] uppercase tracking-wider text-muted-foreground">Private</Badge>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 text-xs gap-1"
            onClick={() => { setEditingCost(null); setAddOpen(true); }}
          >
            <Plus className="w-3.5 h-3.5" /> Add Cost
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Auto-added to line items when this product is selected on a quote or invoice.
        </p>
      </CardHeader>
      <CardContent className="pt-0">
        {isLoading && <div className="text-xs text-muted-foreground py-2">Loading...</div>}

        {!isLoading && (!costs || costs.length === 0) && (
          <div className="text-xs text-muted-foreground py-3 text-center border border-dashed border-border rounded-md bg-muted/20">
            No default costs set.
          </div>
        )}

        {costs && costs.length > 0 && (
          <div className="border rounded-md divide-y bg-background">
            {costs.map(cost => (
              <div key={cost.id} className="p-2.5 text-sm hover:bg-muted/20 group">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="font-medium text-sm truncate">{cost.name}</span>
                    <CostTypeBadge type={cost.type} />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-semibold text-primary">{formatCurrency(cost.totalCost)}</span>
                    <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Tooltip delayDuration={0}>
                        <TooltipTrigger asChild>
                          <button type="button" onClick={() => { setEditingCost(cost); setAddOpen(true); }} className="p-1 text-muted-foreground hover:text-foreground rounded">
                            <Edit className="w-3 h-3" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>Edit</TooltipContent>
                      </Tooltip>
                      <Tooltip delayDuration={0}>
                        <TooltipTrigger asChild>
                          <button type="button" onClick={() => setDeleteTarget(cost)} className="p-1 text-destructive/70 hover:text-destructive rounded">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>Delete</TooltipContent>
                      </Tooltip>
                    </div>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {cost.quantity} {cost.unit || "ea"} @ {formatCurrency(cost.unitCost)}
                  {cost.supplier && <span className="ml-2 px-1 py-0.5 bg-muted rounded border border-border text-xs">Via: {cost.supplier}</span>}
                </div>
                {cost.notes && <div className="text-xs italic text-muted-foreground/70 mt-0.5">{cost.notes}</div>}
              </div>
            ))}
            {totalCost > 0 && (
              <div className="flex justify-between items-center px-2.5 py-2 text-xs font-semibold bg-muted/30">
                <span>Total Default Cost</span>
                <span>{formatCurrency(totalCost)}</span>
              </div>
            )}
          </div>
        )}
      </CardContent>

      <Dialog open={addOpen} onOpenChange={(open) => { setAddOpen(open); if (!open) setEditingCost(null); }}>
        <DialogContent className="sm:max-w-md">
          <DefaultCostForm
            productId={productId}
            cost={editingCost}
            onSuccess={() => { setAddOpen(false); setEditingCost(null); }}
            onCancel={() => { setAddOpen(false); setEditingCost(null); }}
          />
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the default cost from the product template. Existing line items won't be changed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && onDeleteCost(deleteTarget.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

function DefaultCostForm({ productId, cost, onSuccess, onCancel }: {
  productId: number;
  cost: ProductDefaultCost | null;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { formatCurrency } = useSettingsFormat();
  const isEditing = !!cost;
  const [catalogSearch, setCatalogSearch] = useState("");

  const { data: catalog } = useListInternalCatalog();
  const createCost = useCreateProductDefaultCost();
  const updateCost = useUpdateProductDefaultCost();

  const filteredCatalog = catalog?.filter(item =>
    !catalogSearch || item.name.toLowerCase().includes(catalogSearch.toLowerCase())
  );

  const form = useForm<DefaultCostForm>({
    resolver: zodResolver(defaultCostSchema),
    defaultValues: {
      catalogItemId: cost?.catalogItemId ?? null,
      name: cost?.name ?? "",
      quantity: cost?.quantity ?? 1,
      unit: cost?.unit ?? "",
      unitCost: cost?.unitCost ?? 0,
      supplier: cost?.supplier ?? "",
      notes: cost?.notes ?? "",
      type: (cost?.type as "labour" | "materials" | "fees") ?? "materials",
    }
  });

  const selectedCatalogId = form.watch("catalogItemId");
  const selectedCatalogItem = catalog?.find(c => c.id === Number(selectedCatalogId));

  const applyCatalogItem = (item: { id: number; name: string; unitCost: number; unit?: string | null; type: "labour" | "materials" | "fees" }) => {
    form.setValue("catalogItemId", item.id);
    form.setValue("name", item.name);
    form.setValue("unitCost", Number(item.unitCost));
    form.setValue("unit", item.unit ?? "");
    form.setValue("type", item.type);
    setCatalogSearch("");
  };

  const clearCatalogItem = () => {
    form.setValue("catalogItemId", null);
    form.setValue("type", "materials");
  };

  const onSubmit = (data: DefaultCostForm) => {
    const payload = {
      catalogItemId: data.catalogItemId ?? null,
      name: data.name,
      quantity: data.quantity,
      unitCost: data.unitCost,
      unit: data.unit || null,
      supplier: data.supplier || null,
      notes: data.notes || null,
      type: data.type,
    };

    if (isEditing && cost) {
      updateCost.mutate({ productId, costId: cost.id, data: payload }, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListProductDefaultCostsQueryKey(productId) });
          toast({ title: "Default cost updated" });
          onSuccess();
        },
        onError: () => toast({ title: "Failed to update cost", variant: "destructive" }),
      });
    } else {
      createCost.mutate({ productId, data: payload }, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListProductDefaultCostsQueryKey(productId) });
          toast({ title: "Default cost added" });
          onSuccess();
        },
        onError: () => toast({ title: "Failed to add cost", variant: "destructive" }),
      });
    }
  };

  const isPending = createCost.isPending || updateCost.isPending;

  return (
    <>
      <DialogHeader>
        <DialogTitle>{isEditing ? "Edit Default Cost" : "Add Default Cost"}</DialogTitle>
      </DialogHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          {/* Catalog picker */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Cost Catalog <span className="text-destructive">*</span></label>
            {selectedCatalogItem ? (
              <div className="flex items-center justify-between p-3 rounded-md border border-primary/30 bg-primary/5">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm font-medium truncate">{selectedCatalogItem.name}</p>
                    <CostTypeBadge type={selectedCatalogItem.type} />
                  </div>
                  <p className="text-xs text-muted-foreground">{formatCurrency(Number(selectedCatalogItem.unitCost))} per {selectedCatalogItem.unit || "each"}</p>
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={clearCatalogItem}>Change</Button>
              </div>
            ) : (
              <div className="space-y-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                  <Input
                    placeholder="Search cost catalog..."
                    value={catalogSearch}
                    onChange={e => setCatalogSearch(e.target.value)}
                    className="pl-8 text-sm"
                  />
                </div>
                {catalogSearch && filteredCatalog !== undefined && filteredCatalog.length > 0 && (
                  <div className="border rounded-md shadow-sm bg-card overflow-hidden max-h-36 overflow-y-auto">
                    {filteredCatalog.map(c => (
                      <button key={c.id} type="button"
                        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-muted/60 transition-colors border-b last:border-0"
                        onClick={() => applyCatalogItem(c)}>
                        <span className="flex items-center gap-1.5 min-w-0">
                          <span className="text-sm font-medium truncate">{c.name}</span>
                          <CostTypeBadge type={c.type} />
                        </span>
                        <span className="text-sm text-muted-foreground shrink-0 ml-2">{formatCurrency(Number(c.unitCost))}</span>
                      </button>
                    ))}
                  </div>
                )}
                {form.formState.errors.catalogItemId && (
                  <p className="text-sm text-destructive">{form.formState.errors.catalogItemId.message}</p>
                )}
              </div>
            )}
          </div>

          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name *</FormLabel>
                <FormControl>
                  <Input placeholder="e.g. Lumber, Labor, Equipment rental" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="grid grid-cols-2 gap-3">
            <FormField
              control={form.control}
              name="quantity"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Quantity *</FormLabel>
                  <FormControl>
                    <Input type="number" step="any" min="0" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="unit"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Unit</FormLabel>
                  <FormControl>
                    <Input placeholder="each" list="detail-cost-units" {...field} />
                  </FormControl>
                  <datalist id="detail-cost-units">
                    {UNITS.map(u => <option key={u} value={u} />)}
                  </datalist>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="unitCost"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Unit Cost ($) *</FormLabel>
                <FormControl>
                  <Input type="number" step="0.01" min="0" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="supplier"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Supplier</FormLabel>
                <FormControl>
                  <Input placeholder="Optional" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
            <Button type="submit" disabled={isPending || !selectedCatalogItem}>
              {isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : isEditing ? "Save Changes" : "Add Cost"}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </>
  );
}
